import { z } from 'zod';

/**
 * Paramètres d'URL de l'écran Paramètres (`02` §12), refondu le 10/09/2026 :
 * onglets regroupés par domaine plutôt que par
 * technologie — tout ce qui concerne Martelet (FTP, suivi, transporteurs de
 * retour, suspension des envois) vit désormais dans un seul onglet. L'onglet
 * Recharge (`06` §8.6) s'ajoute après Pennylane, puis l'intégration Klaviyo
 * (`06` §11.4).
 *
 * `?store=` reste le drill-down client-side vers la fiche d'une boutique.
 */
export const PARAMETRES_TABS = ['boutiques', 'pennylane', 'recharge', 'klaviyo', 'martelet', 'alertes', 'avance'] as const;

export type ParametresTab = (typeof PARAMETRES_TABS)[number];

/**
 * Anciens onglets, pour que les liens et signets d'avant la refonte tombent
 * au bon endroit plutôt que sur l'onglet par défaut.
 */
const LEGACY_TABS: Readonly<Record<string, ParametresTab>> = {
  general: 'avance',
  ftp: 'martelet',
  maintenance: 'martelet',
};

const parametresSearchSchema = z.object({
  tab: z.string().optional(),
  store: z.string().optional(),
});

export interface ParametresSearch {
  tab: ParametresTab;
  store: string | undefined;
}

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Résout un `?tab=` brut, ancien nom compris ; `boutiques` à défaut. */
export function resolveParametresTab(raw: string | undefined): ParametresTab {
  if (raw === undefined) {
    return 'boutiques';
  }
  if ((PARAMETRES_TABS as readonly string[]).includes(raw)) {
    return raw as ParametresTab;
  }
  return LEGACY_TABS[raw] ?? 'boutiques';
}

/**
 * Lit `tab`/`store` depuis `useSearch({strict:false})` — générique, comme
 * `readTransporteursSearch`, pour rester testable sans monter le vrai arbre de
 * routes.
 */
export function readParametresSearch(rawSearch: unknown): ParametresSearch {
  const parsed = parametresSearchSchema.safeParse(readRecord(rawSearch));
  return {
    tab: resolveParametresTab(parsed.success ? parsed.data.tab : undefined),
    store: parsed.success ? parsed.data.store : undefined,
  };
}

/** `validateSearch` de `routes/parametres.tsx`. */
export function parseParametresSearch(
  search: Record<string, unknown>,
): { tab?: string | undefined; store?: string | undefined } {
  return parametresSearchSchema.parse(search);
}
